import { VRSConst } from "../../app/Constants";
import AuthService from "../../services/AuthService";
import {
  clearIssueToken,
  clearMobileToken,
  clearToken,
  setLoggedInStaff,
  setPermission,
  setToken,
  setUserDetails,
} from "../../utils/TokenManager";
import {
  AUTHENTICATE,
  SET_CURRENT_ISSUE_USER,
  SET_CURRENT_MOBILE_USER,
  SET_CURRENT_USER,
  SET_CURRENT_USER_LOCALE,
  SET_USER_PERMISSION,
} from "./ActionTypes";

/**
 * It dispatches the login reducer and updates the state of current user.
 * @param authData
 * @return {{type, authData: *}}
 */
export function setCurrentUser(authData) {
  return {
    type: SET_CURRENT_USER,
    authData,
  };
}

/**
 * It dispatches the login reducer and updates user permission.
 * @param permissionData
 * @return {{type, permissionData: *}}
 */
export function setUserPermission(permissionData) {
  return {
    type: SET_USER_PERMISSION,
    permissionData,
  };
}

export function setUserLocale(localeData) {
  return {
    type: SET_CURRENT_USER_LOCALE,
    localeData,
  };
}
/**
 * Call reducer to check if the user is already authenticated
 * @param auth
 * @returns {{auth: *, type: *}}
 */
function authenticateSuccess(auth) {
  return {
    type: AUTHENTICATE,
    auth,
  };
}

/**
 * Call reducer to set mobile user
 * @param auth
 * @returns {{auth: *, type: *}}
 */
const setCurrentMobileUser = (auth) => ({
  type: SET_CURRENT_MOBILE_USER,
  auth,
});
const setCurrentIssueUser = (iauth) => ({
  type: SET_CURRENT_ISSUE_USER,
  iauth,
});
/**
 * This method calls Login API and validates the user.
 * If user name password is correct .It dispatches the login action
 */
export const authenticate = async (dispatch) => {
  try {
    const authResponse = await AuthService.doAuthenticate();
    if (authResponse.data.ReasonCode === 0) {
      const tokenResponse = await clearToken();
      if (tokenResponse) {
        // set data to cookie storage
        await setToken(authResponse.data.AccessToken, "JWT");
        await setUserDetails(
          {
            ...authResponse.data.UserDetails,
            LocaleID: authResponse.data.LocaleID,
          },
          "u_details",
        );
        setLoggedInStaff(
          authResponse.data.LoggedInStaffID,
          authResponse.data.LoggedInServicerPassword,
        );
        await setPermission(authResponse.data.Permissions, "permission");
        // Dispatch action to set user details
        dispatch(
          setCurrentUser({
            ...authResponse.data.UserDetails,
            LocaleID: authResponse.data.LocaleID,
          }),
        );
        dispatch(setUserPermission(authResponse.data.Permissions));
        dispatch(setUserLocale(authResponse.data.LocaleID));
      }
      return true;
    }
    if (authResponse.data.ReasonCode === 1004) {
      await clearToken();
      throw authResponse;
    }
  } catch (error) {
    throw error.data;
  }
  return false;
};

/**
 *
 * To logout a user
 * @returns {Promise<void>}
 */

export const logout = async () => {
  await clearToken();
  window.location.replace(`${VRSConst.menuRedirectedURL}/logout`);
  return true;
};

/**
 * Action to check if the user is updated
 * @param auth
 * @param dispatch
 */
export const isAuthenticated = (auth, dispatch) => {
  dispatch(authenticateSuccess(auth));
};

export const tokenRefresh = async () => {
  try {
    const authResponse = await AuthService.doRefresh();
    if (authResponse.data.ReasonCode === 0) {
      // await deleteToken();
      setToken(authResponse.data.Token, "JWT");
    }
    return true;
  } catch (error) {
    throw error.response.data;
  }
};

/**
 * This method calls Login API and validates the mobile dashboard user.
 * If user name password is correct .It dispatches the login action
 *
 * When useSso = true:
 *   - Calls /pwa/authenticate/sso (no password required)
 *   - User is already authenticated via Cognito
 *   - Returns Details and MobilePermissions only (no AccessToken)
 *
 * When useSso = false:
 *   - Calls /pwa/authenticate (requires ServicerID and Password)
 *   - Returns Details, MobilePermissions, and AccessToken (MJWT)
 */
export const mobileAuthenticate = async (userData, dispatch, useSso = true) => {
  try {
    let authResponse;

    if (useSso) {
      // SSO flow: No password required, user authenticated via Cognito
      authResponse = await AuthService.doMobileSsoAuthenticate(userData);
    } else {
      // Legacy flow: Requires ServicerID and Password
      authResponse = await AuthService.doMobileAuthenticate(userData);
    }

    if (authResponse.status === 200 || authResponse.status === "OK") {
      const tokenResponse = await clearMobileToken();

      if (tokenResponse) {
        // Legacy flow: store MJWT if present
        if (!useSso && authResponse.data.AccessToken) {
          await setToken(authResponse.data.AccessToken, "MJWT");
        }

        await setUserDetails(authResponse.data.Details, "Mdetails");
        await setPermission(authResponse.data.MobilePermissions, "Mpermission");

        dispatch(setCurrentMobileUser(authResponse.data.Details));
      }
    }

    return true;
  } catch (error) {
    console.error("Validation Debugger -[mobileAuthenticate] Error occurred:", {
      message: error.message,
      responseStatus: error.response?.status,
      responseData: error.response?.data,
    });
    await clearMobileToken();
    throw error.response ? error.response.data : error;
  }
};

export const issueAuthenticate = async (userData, dispatch) => {
  try {
    const authResponse = await AuthService.doIssueAuthenticate(userData);
    if (authResponse.status === 200 || authResponse.status === "OK") {
      const tokenResponse = await clearIssueToken();
      if (tokenResponse) {
        // set data to cookie storage
        await setToken(authResponse.data.AccessToken, "IJWT");
        await setUserDetails(authResponse.data.Details, "Idetails");
        dispatch(setCurrentIssueUser(authResponse.data.Details));
      }
    }
    // Return the auth response data so callers can access user details
    return authResponse.data;
  } catch (error) {
    await clearIssueToken();
    throw error.response ? error.response.data : error;
  }
};
